import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Button } from "antd";

const VIEWPORT_MARGIN_PX = 12;
const DEFAULT_MAX_WIDTH_PX = 360;

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

export default function ResponsiveConfirm({
  title = "Are you sure?",
  description,
  confirmText = "Yes",
  cancelText = "No",
  onConfirm,
  onCancel,
  disabled = false,
  placement = "bottom", // "bottom" | "top"
  children,
  maxWidthPx = DEFAULT_MAX_WIDTH_PX,
  zIndex = 2000,
}) {
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  const close = useCallback(() => {
    setOpen(false);
    onCancel?.();
  }, [onCancel]);

  const confirm = useCallback(() => {
    setOpen(false);
    onConfirm?.();
  }, [onConfirm]);

  const computePosition = useCallback(() => {
    const triggerEl = triggerRef.current;
    const popEl = popoverRef.current;
    if (!triggerEl || !popEl) return;

    const triggerRect = triggerEl.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    // Constrain width to viewport
    const maxWidth = Math.min(maxWidthPx, viewportW - VIEWPORT_MARGIN_PX * 2);
    popEl.style.maxWidth = `${maxWidth}px`;

    // Measure after maxWidth applied
    const popRect = popEl.getBoundingClientRect();

    // Center over trigger by default, then clamp into viewport
    const preferredLeft =
      triggerRect.left + triggerRect.width / 2 - popRect.width / 2;
    const left = clamp(
      preferredLeft,
      VIEWPORT_MARGIN_PX,
      viewportW - VIEWPORT_MARGIN_PX - popRect.width
    );

    const gap = 8;
    const bottomTop = triggerRect.bottom + gap;
    const topTop = triggerRect.top - gap - popRect.height;

    let top = placement === "top" ? topTop : bottomTop;

    // If requested placement does not fit, flip
    const fitsBottom =
      bottomTop + popRect.height <= viewportH - VIEWPORT_MARGIN_PX;
    const fitsTop = topTop >= VIEWPORT_MARGIN_PX;

    if (placement === "bottom" && !fitsBottom && fitsTop) top = topTop;
    if (placement === "top" && !fitsTop && fitsBottom) top = bottomTop;

    // Final clamp so it never overflows vertically either
    top = clamp(
      top,
      VIEWPORT_MARGIN_PX,
      viewportH - VIEWPORT_MARGIN_PX - popRect.height
    );

    setPos({ top, left, width: popRect.width });
  }, [maxWidthPx, placement]);

  useLayoutEffect(() => {
    if (!open) return;
    computePosition();
  }, [open, computePosition]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (e) => {
      const popEl = popoverRef.current;
      const triggerEl = triggerRef.current;
      if (!popEl || !triggerEl) return;

      const clickedInsidePopover = popEl.contains(e.target);
      const clickedTrigger = triggerEl.contains(e.target);

      if (!clickedInsidePopover && !clickedTrigger) close();
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") close();
    };

    const handleReflow = () => computePosition();

    window.addEventListener("pointerdown", handlePointerDown, true);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleReflow);
    window.addEventListener("scroll", handleReflow, true);

    return () => {
      window.removeEventListener("pointerdown", handlePointerDown, true);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleReflow);
      window.removeEventListener("scroll", handleReflow, true);
    };
  }, [open, close, computePosition]);

  const onTriggerClick = useCallback(
    (e) => {
      if (disabled) return;

      // Keep button behavior predictable
      e.preventDefault();
      e.stopPropagation();

      setOpen((v) => !v);
    },
    [disabled]
  );

  const child = React.Children.only(children);
  const wrappedTrigger = React.cloneElement(child, {
    ref: (node) => {
      triggerRef.current = node;
      const originalRef = child.ref;
      if (typeof originalRef === "function") originalRef(node);
      else if (originalRef && typeof originalRef === "object")
        originalRef.current = node;
    },
    onClick: (e) => {
      child.props?.onClick?.(e);
      onTriggerClick(e);
    },
    "aria-haspopup": "dialog",
    "aria-expanded": open,
  });

  return (
    <>
      {wrappedTrigger}

      {open &&
        createPortal(
          <div
            ref={popoverRef}
            role="dialog"
            aria-modal="false"
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              zIndex,
              width: "max-content",
              maxWidth: `min(${maxWidthPx}px, calc(100vw - ${
                VIEWPORT_MARGIN_PX * 2
              }px))`,
              background: "rgba(20, 20, 20, 0.98)",
              color: "white",
              borderRadius: 10,
              padding: 12,
              boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
            }}
          >
            <div
              style={{
                fontWeight: 700,
                marginBottom: description ? 6 : 10,
                lineHeight: 1.2,
              }}
            >
              {title}
            </div>

            {description ? (
              <div
                style={{ opacity: 0.92, marginBottom: 10, lineHeight: 1.25 }}
              >
                {description}
              </div>
            ) : null}

            <div
              style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
            >
              <Button size="small" onClick={close}>
                {cancelText}
              </Button>
              <Button size="small" type="primary" danger onClick={confirm}>
                {confirmText}
              </Button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
